// src/containers/reportBuilder/MultiProgramPreview.jsx
//
// Multi-program report preview — ported from takso-poc.
//
// Simplifications vs POC:
//   • Charts: all replaced with ChartSlot placeholders (charts TBD)
//   • Word export: omitted (Lambda/docx TBD)
//   • Org preferences API: omitted — defaults used directly
//   • Outcome ratings: omitted (ChartSlot placeholders)
//   • Uses axios (not fetch), calling /report-builder/* endpoints
//
// Three visual layers in each section:
//   DataPanel  (blue)  — aggregate numbers from the DB
//   WriterNote (white) — editable author guidance
//   ChartSlot  (grey)  — chart placeholder

import { Component } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import axios from 'axios';

import {
  REPORT_FONT,
  CollapsibleSection,
  SubHeading,
  ChartTitle,
  TableLabel,
  DataPanel,
  WriterNote,
  AuthorComment,
  ChartSlot,
  Stat,
  LoadingPanel,
  thStyle,
  thStyleR,
  thStyleC,
  tdStyle,
  tdStyleR,
  tdStyleC,
} from '../../components/reports/ReportTemplate';

import ChartHost from '../../components/reports/ChartHost';
import { ReportTintContext } from '../../components/reports/ReportTintContext';
import ImagePickerModal from '../../components/reports/ImagePickerModal';
import CoverImageCropModal from './CoverImageCropModal';
import CustomSectionSlot, { CustomSectionModal } from './CustomSectionSlot';
import { chartApiGet } from '../../lib/chartApi';
import ContractorMap from '../../components/charts/ContractorMap';
import { LocaleService } from '../../services';

// Cover image + custom sections are session-only here (ephemeral) — unlike
// ReportPreview.jsx's single activity, a cohort selection has no stable id
// to persist sReportCustomisation against. Real persistence is planned
// alongside the upcoming "saved period/themed report" entity; until that
// exists, edits reset on reload, matching how the rest of this template
// (WriterNote text, etc.) already behaves with no backend save at all.

// Flattens the category → occupations tree returned by /charts/contractors
// into one row per specific occupation ("Service type"), each still carrying
// its parent category label — the count shown is per occupation, not the
// category total.
const flattenOccupationRows = (categories) =>
  (categories || []).flatMap((cat) =>
    (cat.occupations?.length
      ? cat.occupations
      : [{ occupationCode: null, label: cat.label, count: cat.count }]
    ).map((occ) => ({
      key: `${cat.groupCode ?? 'x'}-${occ.occupationCode ?? 'x'}`,
      count: occ.count,
      categoryLabel: cat.label,
      serviceType: occ.label,
    }))
  );

// ── Formatting helpers ────────────────────────────────────────────────────────

const fmtN = (n) => (n === null ? '—' : LocaleService.fmtLocaleNumeral(n));

const fmtDol = (n) =>
  n === null || n === 0 ? '—' : LocaleService.fmtLocaleCurrencyWholeDollars(n);

const dateRange = (s, e) => {
  const a = LocaleService.fmtLocaleDate(s);
  const b = LocaleService.fmtLocaleDate(e);
  if (!a && !b) return null;
  if (!b || a === b) return a;
  return `${a} – ${b}`;
};

// ── Sub-components ────────────────────────────────────────────────────────────

const STATUS_DB = {
  Unapproved: { label: 'Development', hex: '#EE7A00' },
  Approved: { label: 'Active', hex: '#50972C' },
  Archived: { label: 'Completed', hex: '#1D3498' },
};

const ProgramBadge = ({ statusName }) => {
  const s = STATUS_DB[statusName] || { label: statusName, hex: '#6b7280' };
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        color: '#fff',
        background: s.hex,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {s.label}
    </span>
  );
};

const MiniBar = ({ value, max, color = '#3b82f6', height = 14 }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          height,
          borderRadius: 3,
          background: color,
          width: `${pct}%`,
          minWidth: pct > 0 ? 3 : 0,
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>
        {fmtN(value)}
      </span>
    </div>
  );
};

const GainBadge = ({ gain }) => {
  if (gain == null) return <span style={{ color: '#9ca3af' }}>—</span>;
  const col = gain >= 3 ? '#059669' : gain >= 2 ? '#10b981' : '#34d399';
  return <span style={{ fontWeight: 700, color: col }}>▲ +{gain}</span>;
};

// ── Main component ────────────────────────────────────────────────────────────

class MultiProgramPreview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Per-program linked singles: [{ program, singles: [], loading: true }]
      programData: [],
      // Aggregate summary from /program-summary endpoint
      summary: null,
      summaryLoading: true,
      showDataTint: true,

      // ── Cover image (ephemeral — see header comment) ──────────────────
      coverImg: null, // { url, description, imageCredit, image, crop } | null
      showImagePicker: false,
      imagePickerSlot: null, // null = cover; string = a custom-section slot
      imagePickerMultiple: false,
      pendingCoverImage: null, // { url, description, imageCredit } | null
      showCoverCropModal: false,

      // ── Custom sections (ephemeral) ────────────────────────────────────
      customSections: {}, // { [slot]: { has, heading, text, images } }
      customSectionModalSlot: null,

      // ── Appendix: Contractors (ephemeral — cohort-level, not read from
      // any single activity's own Documentation-tab reportInclusions) ─────
      contractorCategories: [],
      contractorsTableIncluded: true,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const prevIds = (prevProps.selectedActivities || [])
      .map((a) => a.id)
      .join(',');
    const nextIds = (this.props.selectedActivities || [])
      .map((a) => a.id)
      .join(',');
    if (prevIds !== nextIds) {
      this.loadData();
    }
  }

  loadData() {
    const { selectedActivities } = this.props;
    if (!selectedActivities || !selectedActivities.length) {
      this.setState({ programData: [], summary: null, summaryLoading: false });
      return;
    }

    // Initialise per-program state
    this.setState({
      programData: selectedActivities.map((p) => ({
        program: p,
        singles: [],
        loading: true,
      })),
      summary: null,
      summaryLoading: true,
    });

    // Fetch linked singles for each program
    selectedActivities.forEach((program, idx) => {
      axios
        .get('/report-builder/linked-singles', {
          params: { programId: program.id },
        })
        .then((res) => {
          const singles = res.data.ok ? res.data.singles || [] : [];
          this.setState((prev) => ({
            programData: prev.programData.map((item, i) =>
              i === idx ? { ...item, singles, loading: false } : item
            ),
          }));
        })
        .catch(() => {
          this.setState((prev) => ({
            programData: prev.programData.map((item, i) =>
              i === idx ? { ...item, singles: [], loading: false } : item
            ),
          }));
        });
    });

    // Fetch aggregate summary
    const ids = selectedActivities.map((p) => p.id).join(',');
    axios
      .get('/report-builder/program-summary', {
        params: { programIds: ids },
      })
      .then((res) => {
        this.setState({
          summary: res.data.ok ? res.data : null,
          summaryLoading: false,
        });
      })
      .catch(() => {
        this.setState({ summary: null, summaryLoading: false });
      });

    // Fetch contractor occupation-category counts for the whole cohort
    chartApiGet('contractors', { activityIds: ids })
      .then((d) => this.setState({ contractorCategories: d?.categories || [] }))
      .catch(() => this.setState({ contractorCategories: [] }));
  }

  // ── Image picker — shared for cover and custom-section graphics ────────────
  // Sourced from the first selected program's Documents & Media — a cohort
  // selection has no single natural "owning" activity to browse against.
  // Good enough for now; aggregating every selected program's own documents
  // would need either a new endpoint or N parallel fetches merged
  // client-side — not worth it until this needs more than one program's
  // photos to choose from.
  openImagePicker(slot = null, { multiple = false } = {}) {
    this.setState({
      imagePickerSlot: slot,
      imagePickerMultiple: multiple,
      showImagePicker: true,
    });
  }

  handleImagePickerSelect(imgOrImgs) {
    const { imagePickerSlot } = this.state;
    if (!imagePickerSlot) {
      const img = imgOrImgs;
      this.setState({
        showImagePicker: false,
        pendingCoverImage: {
          url: img.url,
          description: img.description || null,
          imageCredit: img.imageCredit || null,
        },
        showCoverCropModal: true,
        imagePickerSlot: null,
        imagePickerMultiple: false,
      });
    } else {
      this.addCustomSectionImages(
        imagePickerSlot,
        Array.isArray(imgOrImgs) ? imgOrImgs : [imgOrImgs]
      );
      this.setState({
        showImagePicker: false,
        imagePickerSlot: null,
        imagePickerMultiple: false,
      });
    }
  }

  handleCoverCropSave({ image, crop }) {
    const source = this.state.pendingCoverImage || this.state.coverImg;
    this.setState({
      coverImg: {
        url: source.url,
        description: source.description || null,
        imageCredit: source.imageCredit || null,
        image,
        crop,
      },
      showCoverCropModal: false,
      pendingCoverImage: null,
    });
  }

  handleCoverCropCancel() {
    this.setState({ showCoverCropModal: false, pendingCoverImage: null });
  }

  // ── Custom sections (see header comment — ephemeral, no backend save) ──────
  openCustomSectionModal(slot) {
    this.setState({ customSectionModalSlot: slot });
  }

  handleAddCustomSection(opts) {
    const slot = this.state.customSectionModalSlot;
    this.setState({ customSectionModalSlot: null });
    if (!slot) return;
    const existing = this.state.customSections[slot];
    const next = {
      has: {
        heading: !!(existing?.has?.heading || opts.heading),
        text: !!(existing?.has?.text || opts.text),
        images: !!(existing?.has?.images || opts.images),
      },
      heading: existing?.heading || '',
      text: existing?.text || '',
      images: existing?.images || [],
    };
    this.setState((prev) => ({
      customSections: { ...prev.customSections, [slot]: next },
    }));
    if (opts.images) {
      this.openImagePicker(slot, { multiple: true });
    }
  }

  saveCustomSectionField(slot, field, value) {
    const existing = this.state.customSections[slot];
    if (!existing) return;
    this.setState((prev) => ({
      customSections: {
        ...prev.customSections,
        [slot]: { ...existing, [field]: value },
      },
    }));
  }

  addCustomSectionImages(slot, imgs) {
    const existing = this.state.customSections[slot];
    if (!existing) return;
    this.setState((prev) => ({
      customSections: {
        ...prev.customSections,
        [slot]: {
          ...existing,
          images: [
            ...(existing.images || []),
            ...imgs.map((img) => ({
              documentId: img.documentId,
              url: img.url,
              name: img.name,
            })),
          ],
        },
      },
    }));
  }

  removeCustomSectionImage(slot, index) {
    const existing = this.state.customSections[slot];
    if (!existing) return;
    this.setState((prev) => ({
      customSections: {
        ...prev.customSections,
        [slot]: {
          ...existing,
          images: (existing.images || []).filter((_, i) => i !== index),
        },
      },
    }));
  }

  removeCustomSection(slot) {
    this.setState((prev) => {
      const next = { ...prev.customSections };
      delete next[slot];
      return { customSections: next };
    });
  }

  // Bundles the common prop set for a <CustomSectionSlot> at a given break.
  customSectionProps(slot) {
    return {
      slot,
      data: this.state.customSections[slot],
      anchorId: `custom-${slot}`,
      onOpenModal: () => this.openCustomSectionModal(slot),
      onHeadingBlur: (text) =>
        this.saveCustomSectionField(slot, 'heading', text),
      onTextBlur: (html) => this.saveCustomSectionField(slot, 'text', html),
      onOpenImagePicker: () => this.openImagePicker(slot, { multiple: true }),
      onRemoveImage: (idx) => this.removeCustomSectionImage(slot, idx),
      onRemoveSection: () => this.removeCustomSection(slot),
    };
  }

  render() {
    const {
      t,
      selectedActivities,
      orgName,
      onEdit,
      prefs = {},
      includedCharts = {},
      chartSettings = {},
      reportAssistEnabled = false,
      assistPrefs = {},
    } = this.props;
    const {
      programData,
      summary,
      summaryLoading,
      coverImg,
      showImagePicker,
      imagePickerMultiple,
      showCoverCropModal,
      pendingCoverImage,
      customSectionModalSlot,
    } = this.state;

    // ── Derive assist sub-preferences ───────────────────────────────────────────
    // assistPrefs overrides the flat reportAssistEnabled for granular control.
    // Each defaults to reportAssistEnabled when the sub-pref key is not set.
    const showAuthorComment =
      assistPrefs.showAuthorComment !== undefined
        ? assistPrefs.showAuthorComment
        : reportAssistEnabled;
    const showWriterNote =
      assistPrefs.showWriterNote !== undefined
        ? assistPrefs.showWriterNote
        : reportAssistEnabled;
    // ── Derive document preferences ─────────────────────────────────────────────
    const doc = prefs?.document || {};
    const cover = doc.cover || {};
    const header = doc.header || {};
    const footer = doc.footer || {};
    const sections = doc.sections || {};

    const fontFamily = doc.fontFamily
      ? `${doc.fontFamily}, sans-serif`
      : 'Arial, Helvetica, sans-serif';
    const bodyFontSize = doc.bodyFontSize ? `${doc.bodyFontSize}pt` : '11pt';

    const showCover = cover.enabled ?? true;
    const showHeader = header.enabled ?? true; // MultiProgramPreview shows header by default
    const showFooter = footer.enabled ?? true; // MultiProgramPreview shows footer by default

    const showTOC = sections.tableOfContents ?? true;
    const showExecSummary = sections.executiveSummary ?? true;
    const showStrategic = sections.strategicAlignment ?? true;
    const showIntended = sections.intendedImpact ?? true;
    const showEvaluation = sections.evaluation ?? true;
    const showAppendices = sections.appendices ?? true;

    // A custom-section heading (if added at that break) becomes its own
    // Contents entry, right after the section it belongs to — no page
    // numbers or anchors here (this template has no pagination system),
    // just its title in the right place in the list.
    const customTocEntry = (slot) =>
      this.state.customSections[slot]?.has?.heading &&
      (this.state.customSections[slot].heading?.trim() ||
        t('report.builder.untitled_section'));

    const tocEntries = [
      showExecSummary &&
        t('report.builder.table_of_contents.executive_summary'),
      showExecSummary && customTocEntry('exec-summary'),
      showStrategic &&
        t('report.builder.table_of_contents.strategic_alignment'),
      showStrategic && customTocEntry('strategic-alignment'),
      showIntended && t('report.builder.table_of_contents.intended_impact'),
      showIntended && customTocEntry('intended-impact'),
      showEvaluation && t('report.builder.table_of_contents.evaluation'),
      showEvaluation && customTocEntry('evaluation'),
      showAppendices &&
        t('report.builder.table_of_contents.appendix_funded_activities'),
      showAppendices && customTocEntry('appendix-activities'),
    ].filter(Boolean);

    // Cover image credit/description caption — same treatment as
    // ReportPreview.jsx's single-activity cover: full-bleed gets a scrim
    // overlay in the photo's bottom-right corner, anything smaller sits
    // plainly below-right of the photo. Offsets are clamped to 0 so a
    // crop that pans/zooms past the frame can't push the caption off the
    // visible box (it would otherwise be clipped by overflow:hidden).
    const coverCaptionText = [coverImg?.description, coverImg?.imageCredit]
      .filter(Boolean)
      .join(' — ');
    const coverCropIsFullBleed =
      !!coverImg?.crop &&
      coverImg.crop.xPct <= 2 &&
      coverImg.crop.yPct <= 2 &&
      coverImg.crop.xPct + coverImg.crop.widthPct >= 98 &&
      coverImg.crop.yPct + coverImg.crop.heightPct >= 98;
    const coverCaptionRightPct = coverImg?.crop
      ? Math.max(0, 100 - (coverImg.crop.xPct + coverImg.crop.widthPct))
      : 0;
    const coverCaptionBottomPct = coverImg?.crop
      ? Math.max(0, 100 - (coverImg.crop.yPct + coverImg.crop.heightPct))
      : 0;
    const coverCaptionTopPct = coverImg?.crop
      ? Math.min(100, coverImg.crop.yPct + coverImg.crop.heightPct)
      : 0;

    const stillLoading = programData.some((p) => p.loading);
    const totalSingles = programData.reduce(
      (sum, p) => sum + p.singles.filter((s) => !s.deactivated).length,
      0
    );
    const numPrograms = selectedActivities.length;
    const today = new Date().toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    // Summary data shortcuts
    const totals = summary?.totals || {};
    const outcomes = summary?.outcomes || [];
    const topEvents = summary?.topEvents || [];
    const outputTypes = summary?.outputTypes || [];
    const progSummaries = summary?.programs || [];
    const goals = summary?.goals || [];

    const highestGainOutcome = outcomes
      .filter((o) => o.gain != null)
      .sort((a, b) => b.gain - a.gain)[0];
    const totalReach = (totals.receptive || 0) + (totals.active || 0);

    const orgDisplay = orgName || t('common.organisation');

    return (
      <div style={{ display: 'grid', gap: 16 }}>
        {/* ── Action bar ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                {t('report.builder.report_preview')}
              </div>
              <button
                onClick={onEdit}
                style={{
                  fontSize: 12,
                  color: '#1d4ed8',
                  background: 'none',
                  border: '1px solid #1d4ed8',
                  borderRadius: 6,
                  padding: '2px 10px',
                  cursor: 'pointer',
                }}
              >
                ← {t('report.builder.selection')}
              </button>
              {/* ✕ Clear button hidden for now */}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              <Trans
                i18nKey="report.builder.num_programs_fmt"
                values={{ count: numPrograms }}
              />
              {!stillLoading && (
                <>
                  {' '}
                  ·{' '}
                  <Trans
                    i18nKey="report.builder.linked_activities"
                    values={{ count: totalSingles }}
                  />
                </>
              )}
              {!summaryLoading && totals.totalGrant > 0 && (
                <>
                  {' '}
                  · <strong>{fmtDol(totals.totalGrant)}</strong>{' '}
                  {t('report.builder.total_grant')}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Document ── */}
        <ReportTintContext.Provider value={this.state.showDataTint}>
          <div
            className="report-doc"
            style={{
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontFamily,
              fontSize: bodyFontSize,
              '--doc-body-size': bodyFontSize,
              lineHeight: 1.7,
              color: '#111827',
              maxWidth: 836,
              overflow: 'visible',
            }}
          >
            {/* Header bar */}
            {showHeader && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 44px',
                  borderBottom: '1px solid #d1d5db',
                  fontSize: 11,
                  color: '#6b7280',
                  background: '#fafafa',
                }}
              >
                <span>{header.leftText || orgDisplay}</span>
                <span>{header.rightText || ''}</span>
              </div>
            )}

            {/* Document body */}
            <div style={{ padding: '36px 44px 28px' }}>
              {/* Cover page */}
              {showCover && (
                <>
                  <div
                    className="mpp-cover"
                    style={{
                      position: 'relative',
                      minHeight: 320,
                      overflow: 'hidden',
                      marginBottom: 36,
                      backgroundColor: coverImg ? undefined : '#f3f4f6',
                    }}
                  >
                    {/* Photo — same independent image/crop math as
                        ReportPreview.jsx's cover (see CoverImageCropModal). */}
                    {coverImg?.crop && coverImg?.image && (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${coverImg.crop.xPct}%`,
                          top: `${coverImg.crop.yPct}%`,
                          width: `${coverImg.crop.widthPct}%`,
                          height: `${coverImg.crop.heightPct}%`,
                          overflow: 'hidden',
                          backgroundColor: '#1f2937',
                        }}
                      >
                        <img
                          src={coverImg.url}
                          alt=""
                          style={{
                            position: 'absolute',
                            left: `${
                              ((coverImg.image.xPct - coverImg.crop.xPct) /
                                coverImg.crop.widthPct) *
                              100
                            }%`,
                            top: `${
                              ((coverImg.image.yPct - coverImg.crop.yPct) /
                                coverImg.crop.heightPct) *
                              100
                            }%`,
                            width: `${
                              (coverImg.image.widthPct /
                                coverImg.crop.widthPct) *
                              100
                            }%`,
                            height: `${
                              (coverImg.image.heightPct /
                                coverImg.crop.heightPct) *
                              100
                            }%`,
                            maxWidth: 'none',
                          }}
                        />
                      </div>
                    )}

                    {/* Credit/description caption — bottom-right of the
                        photo, scrim'd when full-bleed. z-index above the
                        text overlay below since both can occupy the same
                        bottom-right corner once the photo is full-bleed. */}
                    {coverImg?.crop && coverCaptionText && (
                      <div
                        style={
                          coverCropIsFullBleed
                            ? {
                                position: 'absolute',
                                right: `${coverCaptionRightPct}%`,
                                bottom: `${coverCaptionBottomPct}%`,
                                maxWidth: '70%',
                                margin: 8,
                                padding: '3px 8px',
                                background: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: 3,
                                color: '#ffffff',
                                fontSize: 10,
                                lineHeight: 1.4,
                                textAlign: 'right',
                                zIndex: 3,
                              }
                            : {
                                position: 'absolute',
                                right: `${coverCaptionRightPct}%`,
                                top: `${coverCaptionTopPct}%`,
                                maxWidth: '70%',
                                marginTop: 4,
                                color: '#6b7280',
                                fontSize: 10,
                                lineHeight: 1.4,
                                textAlign: 'right',
                                zIndex: 3,
                              }
                        }
                      >
                        {coverCaptionText}
                      </div>
                    )}

                    {/* Change image + Reposition — top-left */}
                    <div className="mpp-cover__top-actions">
                      <button
                        type="button"
                        className="mpp-cover__img-btn"
                        onClick={() => this.openImagePicker(null)}
                        title={
                          coverImg
                            ? t(
                                'report.builder.cover_image_editing.change_cover_image'
                              )
                            : t(
                                'report.builder.cover_image_editing.add_cover_image'
                              )
                        }
                      >
                        {coverImg
                          ? `⇄ ${t('report.builder.cover_image_editing.change_cover_image')}`
                          : `+ ${t('report.builder.cover_image_editing.add_cover_image')}`}
                      </button>
                      {coverImg && (
                        <button
                          type="button"
                          className="mpp-cover__reposition-btn"
                          onClick={() =>
                            this.setState({ showCoverCropModal: true })
                          }
                          title={t(
                            'report.builder.cover_image_editing.reposition_or_zoom'
                          )}
                        >
                          ⤢ {t('report.builder.cover_image_editing.reposition')}
                        </button>
                      )}
                    </div>

                    {/* Remove image — top-right */}
                    {coverImg && (
                      <button
                        type="button"
                        className="mpp-cover__remove-btn"
                        onClick={() => this.setState({ coverImg: null })}
                        title={t('report.builder.remove_cover_image')}
                      >
                        ✕
                      </button>
                    )}

                    {/* Title text — an in-flow centred block when there's no
                        photo (unchanged from before); a bottom-pinned white
                        overlay on a dark scrim once a photo is present, same
                        treatment as ReportPreview.jsx's cover. */}
                    <div
                      style={
                        coverImg
                          ? {
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 2,
                              padding: '28px 20px 20px',
                              background:
                                'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)',
                            }
                          : {
                              minHeight: 320,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                            }
                      }
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: coverImg ? '#e5e7eb' : '#6b7280',
                          marginBottom: 12,
                        }}
                      >
                        {cover.orgName || orgName || ''}
                      </div>
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          color: coverImg ? '#ffffff' : '#111827',
                          marginBottom: 12,
                        }}
                      >
                        {t('report.builder.evaluation_report')}
                      </div>
                      {cover.tagline && (
                        <div
                          style={{
                            fontSize: 15,
                            color: coverImg ? '#f3f4f6' : '#374151',
                          }}
                        >
                          {cover.tagline}
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      borderTop: '2px dashed #e5e7eb',
                      margin: '0 -44px 36px -44px',
                    }}
                  />
                </>
              )}
              {/* Title block */}
              <div style={{ marginBottom: 28 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: 6,
                  }}
                >
                  {t('report.builder.evaluation_report')}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>
                  {orgDisplay}
                </div>
                <div
                  style={{ fontSize: 14, color: '#374151', marginBottom: 2 }}
                >
                  {t('report.builder.num_programs_ul', {
                    count: numPrograms,
                    countFmt: numPrograms,
                  })}
                </div>
                {progSummaries.length > 0 && (
                  <div
                    style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}
                  >
                    {dateRange(
                      progSummaries[0]?.eventStartDate,
                      progSummaries[progSummaries.length - 1]?.eventEndDate
                    )}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  {t('report.builder.prepared')} {today}
                </div>
              </div>

              {/* ── Table of Contents ── */}
              {showTOC && tocEntries.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: 8,
                    }}
                  >
                    {t('report.builder.table_of_contents_label_short')}
                  </div>
                  <ol
                    style={{
                      margin: 0,
                      padding: '0 0 0 20px',
                      fontSize: 13,
                      color: '#374151',
                      lineHeight: 2.2,
                    }}
                  >
                    {tocEntries.map((name, i) => (
                      <li key={i} style={{ color: '#1d4ed8' }}>
                        <span style={{ color: '#374151' }}>{name}</span>
                      </li>
                    ))}
                  </ol>
                  <div
                    style={{ borderTop: '1px solid #e5e7eb', marginTop: 16 }}
                  />
                </div>
              )}

              {/* ── Executive Summary ── */}
              {showExecSummary && (
                <CollapsibleSection
                  title={t('report.builder.executive_summary.title')}
                >
                  {!summaryLoading && totals.events > 0 ? (
                    <>
                      <TableLabel
                        title={t('report.builder.funded_programs_summary')}
                      />
                      <DataPanel>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: '8px 24px',
                            marginBottom: 10,
                          }}
                        >
                          <Stat
                            label={t('report.builder.grant_programs')}
                            value={fmtN(numPrograms)}
                          />
                          <Stat
                            label={t('report.builder.funded_events')}
                            value={fmtN(totals.events)}
                          />
                          <Stat
                            label={t('report.builder.total_reach')}
                            value={fmtN(totalReach)}
                          />
                          <Stat
                            label={t('report.builder.total_grant')}
                            value={fmtDol(totals.totalGrant)}
                          />
                          <Stat
                            label={t('report.builder.inputs.volunteers')}
                            value={fmtN(totals.totalVolunteers)}
                          />
                          <Stat
                            label={t('report.builder.outcomes_measured')}
                            value={fmtN(outcomes.length)}
                          />
                        </div>
                        <div
                          style={{
                            borderTop: '1px solid #bfdbfe',
                            paddingTop: 8,
                          }}
                        >
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: 12,
                            }}
                          >
                            <thead>
                              <tr style={{ borderBottom: '1px solid #bfdbfe' }}>
                                <th style={thStyle}>{t('common.program')}</th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t(
                                    'report.builder.executive_summary.grant_dollars'
                                  )}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('common.activities')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('report.participants')}
                                </th>
                                <th
                                  style={{ ...thStyleC, whiteSpace: 'nowrap' }}
                                >
                                  {t('common.status')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {progSummaries.map((p) => (
                                <tr
                                  key={p.id}
                                  style={{ borderBottom: '1px solid #dbeafe' }}
                                >
                                  <td style={tdStyle}>{p.name}</td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtDol(p.totalGrant)}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {p.linkedCount}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtN(p.receptive + p.active)}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleC,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <ProgramBadge statusName={p.statusName} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </DataPanel>
                    </>
                  ) : (
                    <LoadingPanel loading={summaryLoading} />
                  )}

                  <WriterNote>
                    {showAuthorComment && (
                      <AuthorComment>
                        [
                        {t('report.builder.executive_summary.author_comment', {
                          numEventsFmt: fmtN(totals.events),
                          events: t('report.builder.num_events', {
                            count: totals.events,
                          }),
                          grantRounds: t('report.builder.grant_rounds', {
                            count: numPrograms,
                            countFmt: fmtN(numPrograms),
                          }),
                          totalReach: t('report.builder.people', {
                            count: totalReach,
                            countFmt: fmtN(totalReach),
                          }),
                          volunteers: t('report.builder.volunteers', {
                            count: totals.totalVolunteers,
                            countFmt: fmtN(totals.totalVolunteers),
                          }),
                        })}
                        ]
                      </AuthorComment>
                    )}
                    {showWriterNote && (
                      <p style={{ margin: 0 }}>
                        {t('report.builder.executive_summary.writer_note', {
                          totalReach: fmtN(totalReach),
                        })}
                      </p>
                    )}
                  </WriterNote>

                  <CustomSectionSlot
                    {...this.customSectionProps('exec-summary')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Strategic Alignment ── */}
              {showStrategic && (
                <CollapsibleSection
                  title={t('report.builder.strategic_alignment')}
                >
                  {summaryLoading ? (
                    <LoadingPanel loading={true} />
                  ) : goals.length > 0 ? (
                    <>
                      <SubHeading>
                        {t('report.builder.organisation_goals', {
                          count: goals.length,
                        })}
                      </SubHeading>
                      <DataPanel>
                        {goals.map((g, i) => (
                          <div
                            key={i}
                            style={{
                              marginBottom: i < goals.length - 1 ? 12 : 0,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: REPORT_FONT.body,
                                color: '#111827',
                              }}
                            >
                              {g.goal}
                            </div>
                            {g.description && (
                              <div
                                style={{
                                  fontSize: REPORT_FONT.body,
                                  color: '#3b5998',
                                  marginTop: 3,
                                  lineHeight: 1.55,
                                }}
                              >
                                {g.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </DataPanel>
                    </>
                  ) : null}

                  <SubHeading>
                    {t('report.builder.intended_outcomes.section_title')}
                  </SubHeading>

                  <ChartTitle
                    title={t(
                      'report.builder.intended_outcomes.charts.number_of_funded_activities'
                    )}
                  />
                  {includedCharts.outcomeFrequency ? (
                    <ChartHost
                      chart="outcomeFrequency"
                      programIds={selectedActivities.map((a) => a.id)}
                      reportMode
                    />
                  ) : (
                    <ChartSlot
                      title={t(
                        'report.builder.intended_outcomes.outcome_surveys_conducted'
                      )}
                      description={t('report.builder.enable_show_in_report')}
                    />
                  )}

                  <WriterNote>
                    {showAuthorComment && (
                      <AuthorComment>
                        [{t('report.builder.intended_outcomes.author_comment')}]
                      </AuthorComment>
                    )}
                    {showWriterNote && (
                      <>
                        {outcomes.length >= 1 ? (
                          <Trans
                            i18nKey="report.builder.intended_outcomes.writer_note.considerations"
                            components={{
                              em: <em />,
                              p: <p style={{ margin: '0 0 6px' }} />,
                            }}
                            values={{
                              count: outcomes.length,
                              outcome1: outcomes[0].outcomeName,
                              outcome2: outcomes[1]?.outcomeName,
                            }}
                          />
                        ) : null}
                        <p style={{ margin: 0 }}>
                          {t(
                            'report.builder.intended_outcomes.writer_note.think_about_how_the_program_maps'
                          )}
                        </p>
                      </>
                    )}
                  </WriterNote>

                  <CustomSectionSlot
                    {...this.customSectionProps('strategic-alignment')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Intended Impact ── */}
              {showIntended && (
                <CollapsibleSection
                  title={t('report.builder.intended_impact.title')}
                >
                  {!summaryLoading && (
                    <WriterNote>
                      {showAuthorComment && (
                        <AuthorComment>
                          {outputTypes.length === 0
                            ? [
                                t(
                                  'report.builder.intended_impact.author_comment.no_output_types'
                                ),
                              ]
                            : outputTypes.length === 1
                              ? [
                                  t(
                                    'report.builder.intended_impact.author_comment.one_output_type',
                                    {
                                      count: totals.events,
                                      countFmt: fmtN(totals.events),
                                      outputType:
                                        outputTypes[0].outputType.toLowerCase(),
                                    }
                                  ),
                                ]
                              : [
                                  `${t('report.builder.intended_impact.author_comment.multiple_output_types', { count: totals.events, countFmt: fmtN(totals.events) })}
                                  ${outputTypes
                                    .map(
                                      (ot) =>
                                        `${fmtN(
                                          ot.count
                                        )} ${ot.outputType.toLowerCase()} (${Math.round(
                                          (ot.count / totals.events) * 100
                                        )}%)`
                                    )
                                    .join(
                                      ', '
                                    )}. ${t('report.builder.intended_impact.author_comment.describe_actitivy_types')}`,
                                ]}
                        </AuthorComment>
                      )}
                    </WriterNote>
                  )}

                  <ChartTitle
                    title={t(
                      'report.builder.intended_impact.activity_type_breakdown_chart_title'
                    )}
                  />
                  {includedCharts.activityTypeLadder ? (
                    <ChartHost
                      chart="activityTypeLadder"
                      programIds={selectedActivities.map((a) => a.id)}
                      hideUnused={
                        chartSettings.activityTypeLadder?.hideUnused ?? true
                      }
                      displayView={
                        chartSettings.activityTypeLadder?.displayView ??
                        'grouped'
                      }
                      reportMode
                    />
                  ) : (
                    <ChartSlot
                      title={t(
                        'report.builder.intended_impact.activity_type_breakdown'
                      )}
                      description={t('report.builder.enable_show_in_report')}
                    />
                  )}

                  <WriterNote>
                    {showAuthorComment && (
                      <AuthorComment>
                        [
                        {t(
                          'report.builder.intended_impact.author_comment_theory_of_change'
                        )}
                        ]
                      </AuthorComment>
                    )}
                  </WriterNote>

                  <CustomSectionSlot
                    {...this.customSectionProps('intended-impact')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Evaluation ── */}
              {showEvaluation && (
                <CollapsibleSection title={t('common.evaluation')}>
                  {/* === INPUTS === */}
                  <SubHeading>{t('report.inputs')}</SubHeading>

                  {!summaryLoading && progSummaries.length > 0 ? (
                    <>
                      <TableLabel
                        title={t(
                          'report.builder.investment_and_resourcing_by_program'
                        )}
                      />
                      <DataPanel>
                        <div style={{ overflowX: 'auto' }}>
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: 12,
                            }}
                          >
                            <thead>
                              <tr style={{ borderBottom: '1px solid #bfdbfe' }}>
                                <th style={thStyle}>
                                  {t('report.builder.inputs.program')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('report.builder.inputs.events')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('report.builder.inputs.grant_awarded')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('report.builder.inputs.volunteers')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {progSummaries.map((p) => (
                                <tr
                                  key={p.id}
                                  style={{ borderBottom: '1px solid #dbeafe' }}
                                >
                                  <td style={tdStyle}>{p.name}</td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {p.linkedCount}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtDol(p.totalGrant)}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtN(p.totalVolunteers)}
                                  </td>
                                </tr>
                              ))}
                              <tr
                                style={{
                                  borderTop: '1.5px solid #93c5fd',
                                  fontWeight: 700,
                                }}
                              >
                                <td style={tdStyle}>{t('common.total')}</td>
                                <td
                                  style={{ ...tdStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {fmtN(totals.events)}
                                </td>
                                <td
                                  style={{ ...tdStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {fmtDol(totals.totalGrant)}
                                </td>
                                <td
                                  style={{ ...tdStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {fmtN(totals.totalVolunteers)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </DataPanel>
                    </>
                  ) : (
                    <LoadingPanel loading={summaryLoading} />
                  )}

                  <ChartSlot
                    title={t('report.builder.inputs.investment_by_type')}
                    description={t(
                      'report.builder.inputs.investment_chart_available_in_future'
                    )}
                  />

                  <ChartSlot
                    title={t('common.venues')}
                    description={t(
                      'report.builder.inputs.venue_breakdown_chart_available_in_future'
                    )}
                  />

                  <WriterNote>
                    {showAuthorComment && (
                      <AuthorComment>
                        [
                        {t('report.builder.inputs.author_comment', {
                          volunteerCount: fmtN(totals.totalVolunteers),
                        })}
                        ]
                      </AuthorComment>
                    )}
                  </WriterNote>

                  {/* === OUTPUTS === */}
                  <SubHeading>
                    {t('report.builder.participants.outputs_participation')}
                  </SubHeading>

                  {!summaryLoading && totals.events > 0 ? (
                    <>
                      <TableLabel
                        title={t(
                          'report.builder.participants.participation_by_program'
                        )}
                      />
                      <DataPanel>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(auto-fit, minmax(130px, 1fr))',
                            gap: '8px 24px',
                            marginBottom: 10,
                          }}
                        >
                          <Stat
                            label={t('report.builder.participants.total_reach')}
                            value={fmtN(totalReach)}
                          />
                          <Stat
                            label={t('report.builder.receptive')}
                            value={fmtN(totals.receptive)}
                          />
                          <Stat
                            label={t('common.active')}
                            value={fmtN(totals.active)}
                          />
                          <Stat
                            label={t(
                              'report.builder.participants.receptive_percentage'
                            )}
                            value={
                              totalReach > 0
                                ? Math.round(
                                    (totals.receptive / totalReach) * 100
                                  ) + '%'
                                : '—'
                            }
                          />
                        </div>
                        <div
                          style={{
                            borderTop: '1px solid #bfdbfe',
                            paddingTop: 8,
                          }}
                        >
                          {progSummaries.map((p) => {
                            const r = p.receptive + p.active;
                            return (
                              <div
                                key={p.id}
                                style={{
                                  display: 'flex',
                                  gap: 8,
                                  alignItems: 'center',
                                  fontSize: REPORT_FONT.body,
                                  marginBottom: 3,
                                }}
                              >
                                <span
                                  style={{
                                    width: 220,
                                    flexShrink: 0,
                                    color: '#374151',
                                  }}
                                >
                                  {p.name}
                                </span>
                                <MiniBar
                                  value={r}
                                  max={totalReach}
                                  color="#3b82f6"
                                  height={12}
                                />
                                <span
                                  style={{
                                    color: '#6b7280',
                                    whiteSpace: 'nowrap',
                                    width: 80,
                                    textAlign: 'right',
                                  }}
                                >
                                  {fmtN(r)} {t('common.total').toLowerCase()}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </DataPanel>

                      {topEvents.length > 0 && (
                        <DataPanel tight>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: REPORT_FONT.body,
                              color: '#1e40af',
                              marginBottom: 7,
                            }}
                          >
                            {t(
                              'report.builder.participants.largest_events_by_participant_reach'
                            )}
                          </div>
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: 12,
                            }}
                          >
                            <thead>
                              <tr style={{ borderBottom: '1px solid #bfdbfe' }}>
                                <th style={thStyle}>
                                  {t('report.builder.participants.event')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('report.builder.receptive')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('common.active')}
                                </th>
                                <th
                                  style={{ ...thStyleR, whiteSpace: 'nowrap' }}
                                >
                                  {t('common.total')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {topEvents.map((e) => (
                                <tr
                                  key={e.id}
                                  style={{ borderBottom: '1px solid #dbeafe' }}
                                >
                                  <td style={tdStyle}>{e.name}</td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtN(e.receptive)}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtN(e.active)}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyleR,
                                      fontWeight: 600,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {fmtN(e.receptive + e.active)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </DataPanel>
                      )}
                    </>
                  ) : (
                    <LoadingPanel loading={summaryLoading} />
                  )}

                  <ChartSlot
                    title={t(
                      'report.builder.participants.participant_type_ladder'
                    )}
                    description={t(
                      'report.builder.participants.participant_type_chart_available_later'
                    )}
                  />

                  <WriterNote>
                    {showAuthorComment && (
                      <AuthorComment>
                        [
                        {t('report.builder.participants.author_comments', {
                          value: fmtN(totalReach),
                          events: t('report.builder.num_events', {
                            count: totals.events,
                            countFmt: fmtN(totals.events),
                          }),
                        })}
                        ]
                      </AuthorComment>
                    )}
                  </WriterNote>

                  {/* === OUTCOMES === */}
                  <SubHeading>{t('common.outcomes')}</SubHeading>

                  {outcomes.length > 0 ? (
                    <>
                      <TableLabel
                        title={t(
                          'report.builder.outcomes.outcome_evalution_results'
                        )}
                      />
                      <DataPanel>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: REPORT_FONT.body,
                            color: '#1e40af',
                            marginBottom: 8,
                          }}
                        >
                          {t(
                            'report.builder.outcomes.outcome_evaluation_results_manager_assessed'
                          )}
                        </div>
                        {(() => {
                          const changeGroup = [...outcomes]
                            .filter(
                              (o) => o.avgPre != null && o.avgPost != null
                            )
                            .sort((a, b) => (b.gain || 0) - (a.gain || 0));
                          const effectGroup = [...outcomes]
                            .filter(
                              (o) => o.avgPost != null && o.avgPre == null
                            )
                            .sort(
                              (a, b) => (b.avgPost || 0) - (a.avgPost || 0)
                            );
                          const showSubGroups =
                            changeGroup.length > 0 && effectGroup.length > 0;

                          const subGroupRow = (label, topBorder) => (
                            <tr key={label}>
                              <td
                                colSpan={5}
                                style={{
                                  padding: '6px 6px 3px',
                                  fontSize: REPORT_FONT.th,
                                  fontWeight: 700,
                                  color: '#1e40af',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  borderBottom: '1px solid #bfdbfe',
                                  borderTop: topBorder
                                    ? '1.5px solid #93c5fd'
                                    : undefined,
                                  background: '#dbeafe',
                                }}
                              >
                                {label}
                              </td>
                            </tr>
                          );

                          const outcomeRow = (o) => (
                            <tr
                              key={o.outcomeId}
                              style={{ borderBottom: '1px solid #dbeafe' }}
                            >
                              <td style={tdStyle}>{o.outcomeName}</td>
                              <td style={{ ...tdStyleR, whiteSpace: 'nowrap' }}>
                                {o.evalCount}
                              </td>
                              <td style={{ ...tdStyleR, whiteSpace: 'nowrap' }}>
                                {o.avgPre ?? '—'}
                              </td>
                              <td style={{ ...tdStyleR, whiteSpace: 'nowrap' }}>
                                {o.avgPost ?? '—'}
                              </td>
                              <td style={{ ...tdStyleR, whiteSpace: 'nowrap' }}>
                                <GainBadge gain={o.gain} />
                              </td>
                            </tr>
                          );

                          return (
                            <div style={{ overflowX: 'auto' }}>
                              <table
                                style={{
                                  width: '100%',
                                  borderCollapse: 'collapse',
                                  fontSize: 12,
                                }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      borderBottom: '1px solid #bfdbfe',
                                    }}
                                  >
                                    <th style={thStyle}>
                                      {t('common.outcome')}
                                    </th>
                                    <th
                                      style={{
                                        ...thStyleR,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {t('common.activities')}
                                    </th>
                                    <th
                                      style={{
                                        ...thStyleR,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {t(
                                        'report.builder.outcomes.rating_before'
                                      )}
                                    </th>
                                    <th
                                      style={{
                                        ...thStyleR,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {t(
                                        'report.builder.outcomes.rating_after'
                                      )}
                                    </th>
                                    <th
                                      style={{
                                        ...thStyleR,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {t('report.builder.outcomes.change')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {showSubGroups &&
                                    subGroupRow(
                                      t('report.builder.measures_for_change'),
                                      false
                                    )}
                                  {changeGroup.map(outcomeRow)}
                                  {showSubGroups &&
                                    subGroupRow(
                                      t('report.builder.measure_for_effect'),
                                      true
                                    )}
                                  {effectGroup.map(outcomeRow)}
                                  {!showSubGroups &&
                                    [...changeGroup, ...effectGroup].map(
                                      outcomeRow
                                    )}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: REPORT_FONT.note,
                            color: '#6b7280',
                            fontStyle: 'italic',
                          }}
                        >
                          {t('report.builder.outcome_change_descrip')}
                        </div>
                      </DataPanel>
                    </>
                  ) : (
                    <LoadingPanel loading={summaryLoading} />
                  )}

                  <WriterNote>
                    {showAuthorComment && (
                      <AuthorComment>
                        [
                        {t('report.builder.outcomes_measured_across_events', {
                          outcomes: t('report.builder.num_outcomes', {
                            count: outcomes.length,
                          }),
                          events: t('report.builder.num_events', {
                            count: totals.events,
                            countFmt: fmtN(totals.events),
                          }),
                        })}
                        {highestGainOutcome
                          ? t('report.builder.highest_gain_across_outcomes', {
                              outcomeName: highestGainOutcome.outcomeName,
                              points: t('report.builder.num_points', {
                                count: highestGainOutcome.gain,
                              }),
                              avgPre: highestGainOutcome.avgPre,
                              avgPost: highestGainOutcome.avgPost,
                              events: t('report.builder.num_events', {
                                count: highestGainOutcome.evalCount,
                              }),
                            })
                          : t('report.builder.highest_gain_outcome')}
                        ]
                      </AuthorComment>
                    )}
                    {showWriterNote && (
                      <p style={{ margin: '0 0 6px' }}>
                        {t('report.builder.manager_assessed_writer_note')}
                      </p>
                    )}
                  </WriterNote>

                  <ChartSlot
                    title={t('report.builder.oucome_rating_distributions')}
                    description={t(
                      'report.builder.participant_rating_dist_charts_for_future'
                    )}
                  />

                  <CustomSectionSlot
                    {...this.customSectionProps('evaluation')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Appendix: Funded Activities ── */}
              {showAppendices && (
                <CollapsibleSection
                  title={t('report.builder.appendix_funded_activities')}
                >
                  {stillLoading ? (
                    <LoadingPanel loading={true} />
                  ) : (
                    programData.map((pd) => {
                      const active = pd.singles.filter((s) => !s.deactivated);
                      const deactivated = pd.singles.filter(
                        (s) => s.deactivated
                      );

                      return (
                        <div key={pd.program.id} style={{ marginBottom: 20 }}>
                          <SubHeading>{pd.program.name}</SubHeading>
                          {active.length === 0 && deactivated.length === 0 ? (
                            <div
                              style={{
                                fontSize: 12,
                                color: '#9ca3af',
                                fontStyle: 'italic',
                              }}
                            >
                              {t('report.builder.no_linked_activities_found')}
                            </div>
                          ) : (
                            <table
                              style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: 13,
                              }}
                            >
                              <thead>
                                <tr
                                  style={{ borderBottom: '1px solid #d1d5db' }}
                                >
                                  <th style={{ ...thStyle, width: '32%' }}>
                                    {t('common.activity')}
                                  </th>
                                  <th style={{ ...thStyleC, width: '11%' }}>
                                    {t('common.status')}
                                  </th>
                                  <th style={thStyle}>
                                    {t('report.builder.summary')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {active.map((s) => (
                                  <tr
                                    key={s.id}
                                    style={{
                                      borderBottom: '1px solid #f3f4f6',
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>
                                      {s.name}
                                    </td>
                                    <td style={tdStyleC}>
                                      <span
                                        style={{
                                          fontSize: 9,
                                          fontWeight: 700,
                                          padding: '2px 6px',
                                          borderRadius: 999,
                                          color: '#fff',
                                          background:
                                            s.status === 'Completed'
                                              ? '#1D3498'
                                              : s.status === 'Active'
                                                ? '#50972C'
                                                : '#EE7A00',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {s.status}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        ...tdStyle,
                                        color: s.summary
                                          ? '#374151'
                                          : '#9ca3af',
                                        fontStyle: s.summary
                                          ? 'normal'
                                          : 'italic',
                                      }}
                                    >
                                      {s.summary ||
                                        t(
                                          'report.builder.no_reflection_recorded'
                                        )}
                                    </td>
                                  </tr>
                                ))}
                                {deactivated.map((s) => (
                                  <tr
                                    key={s.id}
                                    style={{
                                      borderBottom: '1px solid #f3f4f6',
                                      verticalAlign: 'top',
                                      opacity: 0.6,
                                    }}
                                  >
                                    <td
                                      style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        color: '#6b7280',
                                      }}
                                    >
                                      {s.name}
                                    </td>
                                    <td style={tdStyleC}>
                                      <span
                                        style={{
                                          fontSize: 9,
                                          fontWeight: 700,
                                          padding: '2px 6px',
                                          borderRadius: 999,
                                          color: '#fff',
                                          background: '#9ca3af',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {t('common.deactivated')}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        ...tdStyle,
                                        color: '#6b7280',
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {t(
                                        'report.builder.activity_did_not_proceed_no_funding'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })
                  )}

                  <CustomSectionSlot
                    {...this.customSectionProps('appendix-activities')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Appendix: Contractors ── */}
              {showAppendices &&
                this.state.contractorCategories.reduce(
                  (s, c) => s + (c.count || 0),
                  0
                ) > 0 && (
                  <CollapsibleSection
                    title={t('report.builder.appendix_contractors')}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 6,
                      }}
                    >
                      <SubHeading>
                        {t('report.builder.contractors_by_occupation')}
                      </SubHeading>
                      <div className="ch__include">
                        <span className="ch__include-label">
                          {t('report.include_in_report')}
                        </span>
                        <div className="ch__include-btns">
                          <button
                            className={`ch__include-btn ch__include-btn--yes ${
                              this.state.contractorsTableIncluded
                                ? 'ch__include-btn--active'
                                : 'ch__include-btn--inactive'
                            }`}
                            onClick={() =>
                              this.setState({ contractorsTableIncluded: true })
                            }
                          >
                            {t('actions.yes')}
                          </button>
                          <button
                            className={`ch__include-btn ${
                              !this.state.contractorsTableIncluded
                                ? 'ch__include-btn--active'
                                : 'ch__include-btn--inactive'
                            }`}
                            onClick={() =>
                              this.setState({
                                contractorsTableIncluded: false,
                              })
                            }
                          >
                            {t('actions.no')}
                          </button>
                        </div>
                      </div>
                    </div>
                    {this.state.contractorsTableIncluded && (
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                          marginBottom: 16,
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: '1px solid #d1d5db' }}>
                            <th style={{ ...thStyleC, width: '15%' }}>
                              {t('report.count')}
                            </th>
                            <th style={thStyle}>
                              {t(
                                'report.builder.contractors.occupation_category'
                              )}
                            </th>
                            <th style={thStyle}>
                              {t('report.builder.contractors.service_type')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {flattenOccupationRows(
                            this.state.contractorCategories
                          ).map((r, i) => (
                            <tr
                              key={r.key}
                              style={{ borderBottom: '1px solid #f3f4f6' }}
                            >
                              <td style={tdStyleC}>{r.count}</td>
                              <td style={tdStyle}>{r.categoryLabel}</td>
                              <td style={tdStyle}>{r.serviceType}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <ContractorMap
                      activityIds={selectedActivities.map((a) => a.id)}
                      showReportToggle
                    />
                  </CollapsibleSection>
                )}

              {/* Summary line */}
              {!stillLoading && (
                <div
                  style={{
                    borderTop: '2px solid #111827',
                    paddingTop: 10,
                    marginTop: 24,
                    display: 'flex',
                    gap: 28,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    <Trans
                      i18nKey="report.builder.num_programs"
                      components={{
                        strong: <strong style={{ color: '#111827' }} />,
                      }}
                      values={{
                        count: numPrograms,
                        countFmt: fmtN(numPrograms),
                      }}
                    />
                  </span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    <Trans
                      i18nKey="report.builder.num_linked_activities"
                      components={{
                        strong: <strong style={{ color: '#111827' }} />,
                      }}
                      values={{
                        count: totalSingles,
                        countFmt: fmtN(totalSingles),
                      }}
                    />
                  </span>
                  {!summaryLoading && totals.totalGrant > 0 && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      <strong style={{ color: '#111827' }}>
                        {fmtDol(totals.totalGrant)}
                      </strong>{' '}
                      {t('report.builder.total_grant')}
                    </span>
                  )}
                  {!summaryLoading && totalReach > 0 && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      <strong style={{ color: '#111827' }}>
                        {fmtN(totalReach)}
                      </strong>{' '}
                      {t('report.builder.total_reach')}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Footer bar */}
            {showFooter && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 44px',
                  borderTop: '1px solid #d1d5db',
                  fontSize: 11,
                  color: '#6b7280',
                  background: '#fafafa',
                }}
              >
                <span style={{ flex: 1 }} />
                <span style={{ flex: 1, textAlign: 'center' }}>
                  {footer.centreText ||
                    t('report.builder.prepared_using_takso')}
                </span>
                <span style={{ flex: 1, textAlign: 'right' }}>
                  {t('report.builder.page_1')}
                </span>
              </div>
            )}
          </div>
        </ReportTintContext.Provider>

        {/* Image picker — shared for cover and custom-section graphics.
            Sourced from the first selected program's Documents & Media (see
            openImagePicker's own comment for why). */}
        <ImagePickerModal
          open={showImagePicker}
          projectId={selectedActivities?.[0]?.id}
          multiple={imagePickerMultiple}
          onSelect={(img) => this.handleImagePickerSelect(img)}
          onClose={() =>
            this.setState({
              showImagePicker: false,
              imagePickerSlot: null,
              imagePickerMultiple: false,
            })
          }
        />
        {/* Custom-section choice modal — shared across every break's
            "Add custom section (optional)" button. */}
        <CustomSectionModal
          open={!!customSectionModalSlot}
          onCancel={() => this.setState({ customSectionModalSlot: null })}
          onAdd={(opts) => this.handleAddCustomSection(opts)}
        />
        {/* Cover image crop modal — aspectRatio matches this template's own
            cover box (shorter/wider than ReportPreview.jsx's full A4 page),
            so what's previewed here matches where it actually renders. */}
        <CoverImageCropModal
          open={showCoverCropModal}
          imageUrl={pendingCoverImage?.url || coverImg?.url}
          initialImage={pendingCoverImage ? undefined : coverImg?.image}
          initialCrop={pendingCoverImage ? undefined : coverImg?.crop}
          aspectRatio={748 / 320}
          onCancel={() => this.handleCoverCropCancel()}
          onSave={(payload) => this.handleCoverCropSave(payload)}
        />
      </div>
    );
  }
}

MultiProgramPreview = withTranslation()(MultiProgramPreview);

export default MultiProgramPreview;
